import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const ROLE_CONFIG = {
  citizen: { label: 'Citizen', icon: '👤', color: '#00C896', route: '/citizen' },
  responder: { label: 'Responder', icon: '🚒', color: '#FFB300', route: '/responder' },
  admin: { label: 'Admin / Commander', icon: '⚡', color: '#FF3B3B', route: '/admin' },
};

export default function LoginModal({ role, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cfg = ROLE_CONFIG[role];

  const validate = () => {
    const errs = {};
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Valid email required';
    if (!password || password.length < 6) errs.password = 'Min 6 characters';
    if (isRegister && !name.trim()) errs.name = 'Name required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, role, name);
        toast.success('Account created! Welcome to AegisNet');
      } else {
        await login(email, password);
        toast.success(`Welcome back, ${role}`);
      }
      navigate(cfg.route);
    } catch (err) {
      const msg = err.code === 'auth/user-not-found'
        ? 'Account not found'
        : err.code === 'auth/wrong-password'
        ? 'Incorrect password'
        : err.code === 'auth/email-already-in-use'
        ? 'Email already registered'
        : 'Authentication failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(10,15,30,0.85)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="glass-card w-full max-w-md p-8 relative"
          onClick={(e) => e.stopPropagation()}
          style={{ borderColor: cfg.color + '40' }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
            aria-label="Close"
          >
            ✕
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-3"
              style={{ background: cfg.color + '20', border: `1px solid ${cfg.color}40` }}
            >
              {cfg.icon}
            </div>
            <h2 className="font-display font-bold text-xl" style={{ color: cfg.color }}>
              {cfg.label} {isRegister ? 'Registration' : 'Login'}
            </h2>
            <p className="text-gray-400 text-sm mt-1">AegisNet AI — Emergency Response Platform</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {isRegister && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full bg-navy border border-navy-border rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-electric transition-colors"
                  aria-label="Full name"
                />
                {errors.name && <p className="text-crimson text-xs mt-1">{errors.name}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-navy border border-navy-border rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-electric transition-colors"
                aria-label="Email"
                autoComplete="email"
              />
              {errors.email && <p className="text-crimson text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1">{t('password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-navy border border-navy-border rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-electric transition-colors"
                aria-label="Password"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
              />
              {errors.password && <p className="text-crimson text-xs mt-1">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-display font-bold tracking-wider text-navy transition-all hover:brightness-110 active:scale-98 disabled:opacity-60"
              style={{ background: cfg.color }}
              aria-busy={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="spinner w-4 h-4" />
                  {t('signingIn')}
                </span>
              ) : (
                `${isRegister ? 'Create Account' : t('login')} as ${cfg.label}`
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-4">
            {isRegister ? 'Already have an account?' : 'New to AegisNet?'}{' '}
            <button
              onClick={() => { setIsRegister(!isRegister); setErrors({}); }}
              className="text-electric hover:underline font-semibold"
            >
              {isRegister ? 'Sign in' : 'Register'}
            </button>
          </p>

          <p className="text-center text-xs text-gray-500 mt-3">
            🔒 Secured by Firebase Authentication
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
